import { useEffect, useState } from 'react';
import {
  Flex, Box, Text,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverCloseButton,
  PopoverHeader,
  PopoverBody,
  Portal,
  IconButton,
  Button,
  Icon,
  HStack,
  useDisclosure,
  VStack,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Wrap,
} from '@chakra-ui/react';
import { FiInfo, FiChevronDown } from 'react-icons/fi';
import { useRecoilState, useRecoilValue } from 'recoil';
import { selectedDatabaseIdState } from '@transport-insights/uikit';

import {
  useMapData,
} from '@src/modules/performance/context/map-api-hooks';
import LoadingSpinner from '@src/components/shared/LoadingSpinner';
import MapLegend from './components/MapLegend';
import RCAMap from './components/RCAMap';
import { selectedMapViewState } from '../../state';

const menuOptions = [
  { key: 'safety', label: 'Road Safety', excludedIds: [] },
  { key: 'amenityCondition', label: 'Road Condition', excludedIds: [] },
  { key: 'activity', label: 'Activity Management', excludedIds: [81] },
  { key: 'coassurance', label: 'Co-Investor Assurance', excludedIds: [81] },
  { key: 'servicePerformance', label: 'Service Performance', excludedIds: [81] },
  { key: 'dataQuality', label: 'Data Quality', excludedIds: [] },
];

const servicePerformanceFilters = [
  {
    value: 'Road safety',
    label: 'Road safety',
  },
  {
    value: 'Condition of the sealed road network',
    label: 'Condition of the sealed road network',
  },
  {
    value: 'Maintenance of the sealed road network',
    label: 'Maintenance of the sealed road network',
  },
  {
    value: 'Condition of the footpaths within the local road network',
    label: 'Condition of the footpaths within the local road network',
  },
  {
    value: 'Response to service requests',
    label: 'Response to service requests',
  },
];

const dataQualityFilters = [
  {
    value: '',
    label: 'At expected standard',
  },
  {
    value: 'Accuracy',
    label: 'Accuracy',
  },
  {
    value: 'Completeness',
    label: 'Completeness',
  },
  {
    value: 'Timeliness',
    label: 'Timeliness',
  },
  {
    value: 'High',
    label: 'High Importance',
  },
  {
    value: 'Moderate',
    label: 'Moderate Importance',
  },
  {
    value: 'Low',
    label: 'Low Importance',
  },
  {
    value: 'Network',
    label: 'Network',
  },
  {
    value: 'Asset Inventory',
    label: 'Asset Inventory',
  },
  {
    value: 'Condition',
    label: 'Condition',
  },
  {
    value: 'Maintenance Activity',
    label: 'Maintenance Activity',
  },
  {
    value: 'Demand/Use',
    label: 'Demand/Use',
  },
  {
    value: 'Crash',
    label: 'Crash',
  },
  {
    value: 'Score',
    label: 'Score',
  },
];

const activityFilters = [
  {
    value: 'Overall',
    label: 'Overall',
  },
  {
    value: 'Average_score_Pillars_of_Success',
    label: 'Planning Quality',
  },
  {
    value: 'Average_score_TIO',
    label: 'Co-investment planning quality',
  },
  {
    value: 'Smart_buyer_selfassessment',
    label: 'Smart Buyer',
  },
];

function FilterMenu({ options, selected, handleOnChange }) {
  return (
    <Box zIndex="100" mb={2}>
      <Text fontSize="xs" mb={1} color="gray.600" fontWeight="bold">Filter data by:</Text>
      <Wrap spacing={1}>
        {options.map((o, i) => (
          <Box
            key={i}
            as="button"
            fontSize="xs"
            variant="unstyled"
            px={2}
            py={0}
            borderRadius="full"
            cursor="pointer"
            onClick={() => handleOnChange(o.value)}
            backgroundColor={selected === o.value ? 'gray.500' : 'white'}
            color={selected === o.value ? 'white' : 'gray.500'}
            _hover={{ bg: selected !== o.value ? 'gray.300' : '', color: selected !== o.value ? 'white' : 'white' }}
            transition="all 0.2s"
          >
            {o.label}
          </Box>
        ))}
      </Wrap>
    </Box>
  );
}

function DescriptionPopover({ selected }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  let content = '';
  let title = '';

  switch (selected) {
    case 'safety':
      title = 'Road Safety';
      content = (
        <Text fontSize="sm">
          The map shows the 5 year trend of Fatal and Serious Injuries for the regions or Territorial Authorities in New Zealand. The legend explains the relevance of the green, amber and red indicators. See the Road Safety zone within the Performance Dashboard for further details.
        </Text>
      );
      break;
    case 'amenityCondition':
      title = 'Road Condition';
      content = (
        <Text fontSize="sm">
          The map shows how vehicle travel on &apos;smooth&apos; sealed roads is trending for the regions or Territorial Authorities in New Zealand. This indicates the trend of the ride quality experienced by road users over the last 5 years. The legend explains the relevance of the green, amber and red indicators. See the Road Condition zone within the Performance Dashboard for further details.
        </Text>
      );
      break;
    case 'activity':
      title = 'Activity Management';
      content = (
        <>
          <Text mb={2} fontSize="sm">
            The map shows for the Territorial Authorities in New Zealand, a composite indicator of their Activity Management Planning results, Smart buyer self-assessment, and Data Quality results. See the Activity Management zone within the Performance Dashboard for further details.
          </Text>
          <Text fontSize="sm">
            When viewing for a region, the map shows the region boundaries to indicate the TAs in the regions.
          </Text>
        </>
      );
      break;
    case 'servicePerformance':
      title = 'Service Performance';
      content = (
        <>
          <Text mb={2} fontSize="sm">
            The map shows for the 67 Territorial Authorities in New Zealand, the annual results for the five DIA Roads and Footpaths mandatory non-financial performance measures, as published in each Authority’s Annual Report.
          </Text>
          <Text mb={2} fontSize="sm">The Levels of Service for the five measures are set individually by Territorial Authorities and will differ across the country.</Text>
          <Text fontSize="sm" mb={2}>
            You can select each of the five measures from the drop down on the map. See the Service Performance zone within the Performance Dashboard for further details.
          </Text>
          <Text fontSize="sm">
            When viewing for a region, the map shows the region boundaries to indicate the TAs in the regions.
          </Text>
        </>
      );
      break;
    case 'coassurance':
      title = 'Co-Investor Assurance';
      content = (
        <>
          <Text mb={2} fontSize="sm">
            The map shows the results from the latest Local Authority technical audits carried out by NZTA under Section 95(1)(e)(ii) of the Land Transport Management Act 2003. Two types of investment audits are typically undertaken, a Technical audit and a Procedural audit.
          </Text>
          <Text mb={2} fontSize="sm">The map also shows the month and year of the latest audit for each Territorial Authority.</Text>
          <Text fontSize="sm" mb={2}>See the Co-Investor Assurance zone within the Performance Dashboard for further details.</Text>
          <Text fontSize="sm">
            When viewing for a region, the map shows the region boundaries to indicate the TAs in the regions.
          </Text>
        </>
      );
      break;
    case 'dataQuality':
      title = 'Data Quality';
      content = (
        <>
          <Text mb={2} fontSize="sm">
            The map shows for each of the 67 Territorial Authorities in New Zealand, the percentage of asset management data quality metrics
            achieving the expected standard. You can select the different levels of importance, dimensions and categories using the drop down on the map.
            See the Data Quality Dashboard and Metric Library for further details.
          </Text>
          <Text fontSize="sm" mb={2}>The results exclude Data Quality results for State Highways.</Text>
          <Text fontSize="sm">
            When viewing for a region, the map shows the region boundaries to indicate the TAs in the regions.
          </Text>
        </>
      );
      break;
    default:
      break;
  }

  if (!content) return null;

  return (
    <Popover
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      placement="auto"
      isLazy
    >
      <PopoverTrigger>
        <IconButton
          variant="ghost"
          colorScheme="blue"
          icon={<Icon as={FiInfo} boxSize="18px" />}
          aria-label="info"
          size="xs"
          zIndex="10!important"
          _focus={{ boxShadow: 'none' }}
          className="hidden-print"
        />
      </PopoverTrigger>
      <Portal>
        <Box position="relative" zIndex="999">
          <PopoverContent maxW="600px" width="fit-content" zIndex={4} _focus={{ boxShadow: 'none' }}>
            <PopoverArrow />
            <PopoverCloseButton />
            <PopoverHeader fontWeight="bold">{title}</PopoverHeader>
            <PopoverBody>{content}</PopoverBody>
          </PopoverContent>
        </Box>
      </Portal>
    </Popover>
  );
}

const defaultFilters = {
  servicePerformance: servicePerformanceFilters[0].value,
  dataQuality: dataQualityFilters[0].value,
  activity: activityFilters[0].value,
};

function MapPage({ headerHeight }) {
  const rcaId = useRecoilValue(selectedDatabaseIdState);
  const isRegionOrNational = rcaId >= 1000 || rcaId === 81;
  const [currentSelection, setCurrentSelection] = useRecoilState(selectedMapViewState);
  const [filter, setFilter] = useState({ ...defaultFilters });
  const { isLoading: isLoadingMap } = useMapData(currentSelection, filter[currentSelection]);
  const selectedLabel = menuOptions.find((opt) => opt.key === currentSelection)?.label ?? 'Select Option';
  const isLoading = isLoadingMap || !rcaId;
  const filteredMenuOptions = menuOptions.filter((option) => !option.excludedIds.includes(rcaId));

  useEffect(() => {
    if (currentSelection && defaultFilters[currentSelection] !== undefined) {
      setFilter((prev) => ({
        ...prev,
        [currentSelection]: defaultFilters[currentSelection],
      }));
    }
  }, [currentSelection]);

  useEffect(() => {
    if (rcaId && currentSelection) {
      // If the current selection is excluded for this RCA, change it to the first available option
      const currentOption = menuOptions.find((opt) => opt.key === currentSelection);
      if (currentOption?.excludedIds.includes(rcaId)) {
        setCurrentSelection(filteredMenuOptions[0].key);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rcaId]);

  const renderFilters = () => {
    switch (currentSelection) {
      case 'servicePerformance':
        return (
          <FilterMenu
            options={servicePerformanceFilters}
            selected={filter.servicePerformance}
            handleOnChange={(val) => setFilter({ ...filter, servicePerformance: val })}
          />
        );
      case 'dataQuality':
        return (
          <FilterMenu
            options={dataQualityFilters}
            selected={filter.dataQuality}
            handleOnChange={(val) => setFilter({ ...filter, dataQuality: val })}
          />
        );
      case 'activity':
        return (
          <FilterMenu
            options={activityFilters}
            selected={filter.activity}
            handleOnChange={(val) => setFilter({ ...filter, activity: val })}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Flex
      width="100%"
      height={`calc(100vh - ${headerHeight + 110}px)`}
      minHeight="500px"
      padding={4}
      background="white"
      boxShadow="base"
      borderRadius="md"
      flexDirection="column"
      position="sticky"
      top={`${isRegionOrNational ? headerHeight - 16 : headerHeight + 40}px`}
    >
      <Text fontSize="md" fontWeight="bold">Comparative Map</Text>
      <Text fontSize="sm" mb={2} color="gray.500">
        Select a topic from the dropdown to see the data visualised on the map.
      </Text>

      <Flex direction="column" height="100%" flexGrow="1">
        <Flex flexGrow="1" position="relative" flexDirection="column" overflow="hidden">
          <Box position="absolute" top={2} right={2} left={2} zIndex="200">
            <VStack
              spacing={2}
              mb={2}
              align="flex-start"
              background="whiteAlpha.700"
              backdropFilter="blur(4px)"
              borderRadius="md"
              boxShadow="sm"
              width="100%"
              padding={2}
            >
              <HStack alignItems="center">
                <Menu placement="bottom-start">
                  <MenuButton
                    as={Button}
                    size="sm"
                    fontSize="md"
                    variant="ghost"
                    background="gray.800"
                    color="white"
                    transition="background-color 0.2s"
                    _hover={{ background: 'gray.500' }}
                    _active={{ background: 'gray.900' }}
                    _focus={{ boxShadow: 'none' }}
                    rightIcon={<Icon as={FiChevronDown} boxSize={4} />}
                  >
                    {selectedLabel}
                  </MenuButton>
                  <MenuList zIndex="1000">
                    {filteredMenuOptions.map((option) => (
                      <MenuItem
                        key={option.key}
                        onClick={() => setCurrentSelection(option.key)}
                        bg={currentSelection === option.key ? 'gray.100' : 'transparent'}
                        _hover={{ bg: 'gray.50' }}
                        _focus={{ bg: 'gray.50' }}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
                <DescriptionPopover selected={currentSelection} />
              </HStack>
              {renderFilters()}
              <MapLegend selection={currentSelection} filter={filter} />
            </VStack>
          </Box>
          {isLoading
            && (
            <Box width="100%" height="100%" position="absolute" left="0" top="0" zIndex="150">
              <LoadingSpinner zIndex="150" height="100%" position="relative" />
              <Box
                backgroundColor="gray.100"
                width="100%"
                height="100%"
                position="absolute"
                left="0"
                top="0"
                zIndex="100"
                opacity=".85"
              />
            </Box>
            )}
          <RCAMap currentSelection={currentSelection} filter={filter[currentSelection]} />
        </Flex>
      </Flex>
    </Flex>
  );
}

export default MapPage;
